import { FieldValue, TSubmitValues } from "backend/datainput";
import * as wf from "client-api-workflow";
import { clearElementCache } from "qpr-base/querybuilder/ElementCache";
import React from "react";
import { useMutation, useQueryClient } from "react-query";

const useSubmitElementValues = () => {
  const queryClient = useQueryClient();
  const invalidateCache = React.useMemo(
    () => ({
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.indexOf("data-") >= 0,
        });
      },
    }),
    [queryClient]
  );
  // was: types.MeasurePayload

  return useMutation(async (data: TSubmitValues) => {
    const dataPerWorkflow: Record<string, FieldValue[]> = {};
    const addField = (id: string, value: FieldValue) => {
      if (!dataPerWorkflow[id]) dataPerWorkflow[id] = [];
      dataPerWorkflow[id].push(value);
    };
    data.fields.forEach((f) => {
      if (f.field.element?.workflowInstance?.wfInstanceId) {
        addField(f.field.element?.workflowInstance?.wfInstanceId, f);
      }
    });

    const workflowIdsToSend = Object.keys(dataPerWorkflow);

    // eslint-disable-next-line
    for (const wfId of workflowIdsToSend) {
      const fields = dataPerWorkflow[wfId];
      const [f1] = fields;
      const firstInstance = f1.field.element?.workflowInstance;
      if (!firstInstance) {
        continue;
      }

      /**
       * Collect comment entries (Actions and Recommendsations, Justification etc.)
       */

      if (data.wf && data.wf.allowedActions.includes("editcomments")) {
        const commentEntries = fields.filter(
          (f) => f.field.inputType === "comment"
        );

        console.log("Comment Entries", commentEntries);

        for (const comment of commentEntries) {
          console.log("POST Comment Entries", comment);

          if (comment.field.comments && comment.field.comments.length) {
            const [orignalComment] = comment.field.comments;
            console.log("updateComment ", comment);
            await wf.updateComment({
              commentId: orignalComment.id,
              comment: comment.value,
              wfId,
            });
          } else {
            await wf.createComment({
              comment: comment.value,
              parentId: firstInstance.meaId,
              period: firstInstance.period,
            });
          }
          queryClient.invalidateQueries({
            predicate: (query) => query.queryKey.indexOf("comments-") >= 0,
          });
        }
      }

      /**
       * Collect data for Element Series
       */

      const clearElements: Record<string, true> = {};

      let seriesToSend: {
        value: string;
        period: string;
        series: string;
        elementId: string;
      }[] = [];
      if (data.wf && data.wf.allowedActions.includes("editvalues")) {
        seriesToSend = [
          ...fields
            .filter((value) => value.series)
            .filter((value) => value.series !== "MESSAGE")
            .map((item) => {
              if (item.field.element?.id) {
                clearElements[item.field.element?.id] = true;
              }
              return {
                value: item.value,
                series: item.series || "",
                period: firstInstance.period || "",
                elementId: item.field.element?.id || "",
              };
            }),
        ];
      }

      if (data.transition === "SUBMIT" || !data.transition) {
        seriesToSend.push({
          value: "0",
          period: firstInstance.period || "",
          series: "STATUS",
          elementId: firstInstance?.meaId || "",
        });
      }

      const allFiles: File[] = [];
      if (data.wf && data.wf.allowedActions.includes("editdocuments")) {
        fields
          .filter((f) => f.field.inputType === "attachments")
          .forEach((item) => {
            item.files?.forEach((file) => {
              allFiles.push(file);
            });
          });
      }

      if (allFiles.length > 0) {
        const attachmentsData = {
          parentId: firstInstance.meaId,
          period: firstInstance.period,
          files: allFiles,
        };
        console.log("attachmentsData", attachmentsData);
        await wf.createAttachments(attachmentsData);
      }

      if (seriesToSend.length > 0) {
        const res = await wf.postMeasureValues({
          wfId: firstInstance.wfInstanceId,
          values: seriesToSend,
        });
        clearElementCache(Object.keys(clearElements));
      }
      const clearIdList = Object.keys(clearElements);
      if (clearIdList.length > 0) {
        clearElementCache(clearIdList);
      }
      return await wf.updateWorkflowInstanceState({
        id: firstInstance.wfInstanceId,
        transition: {
          transition: data.transition || "SUBMIT",
          message: data.message || "Data submitted",
        },
      });
    }
  }, invalidateCache);
};

export default useSubmitElementValues;
